/**
 * FleetOrderRowSkeleton Component
 *
 * Skeleton loading placeholder for FleetOrderRow.
 * Matches the structure and layout of the actual order card (COMPACT design).
 *
 * @module components/FleetOrderRowSkeleton
 */

import { useEffect, useState } from "react"
import { Dimensions, View, ViewStyle } from "react-native"

import { colors, spacing } from "@/theme"

import { SkeletonShimmer } from "./SkeletonShimmer"

export interface FleetOrderRowSkeletonProps {
  /**
   * Optional style override
   */
  style?: ViewStyle
}

/**
 * FleetOrderRowSkeleton Component
 *
 * Displays animated skeleton placeholder matching FleetOrderRow COMPACT layout.
 * Includes mobile-responsive layouts.
 */
export function FleetOrderRowSkeleton({ style: $styleOverride }: FleetOrderRowSkeletonProps) {
  // Mobile responsiveness
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const updateLayout = () => {
      const { width } = Dimensions.get("window")
      setIsMobile(width < 768)
    }

    updateLayout()
    const subscription = Dimensions.addEventListener("change", updateLayout)
    return () => subscription?.remove()
  }, [])

  return (
    <View style={[$cardOuter, $styleOverride]}>
      {/* Card content */}
      <View style={$cardInner}>
        <View style={isMobile ? $contentContainerMobile : $contentContainer}>
          {/* Left Section: Order Number & Number Plate */}
          <View style={isMobile ? $leftSectionMobile : $leftSection}>
            <View style={$leftContent}>
              {/* Order number */}
              <SkeletonShimmer width={100} height={14} borderRadius={4} style={$orderNumber} />
              {/* Number plate skeleton */}
              <SkeletonShimmer
                width={isMobile ? 160 : 135}
                height={isMobile ? 36 : 32}
                borderRadius={4}
              />
            </View>
          </View>

          {/* Center Section: Status & Details */}
          <View style={$centerSection}>
            {/* Progress Bar - simplified single bar */}
            <SkeletonShimmer width="100%" height={8} borderRadius={4} style={$progressBar} />

            {/* Phase Text */}
            <SkeletonShimmer width={180} height={14} borderRadius={4} />

            {/* Date row with icon placeholder */}
            <View style={$dateRow}>
              <SkeletonShimmer width={20} height={20} borderRadius={10} />
              <SkeletonShimmer width={120} height={14} borderRadius={4} />
            </View>

            {/* Timing Info (pickup/return times) */}
            <SkeletonShimmer width={220} height={14} borderRadius={4} />

            {/* Inspection Badge */}
            <SkeletonShimmer width={120} height={28} borderRadius={8} style={$badge} />
          </View>

          {/* Right Section: Action Buttons (stacked vertically) */}
          <View style={isMobile ? $rightSectionMobile : $rightSection}>
            {/* Multiple button skeletons stacked vertically */}
            <SkeletonShimmer width={isMobile ? 80 : "100%"} height={24} borderRadius={6} />
            <SkeletonShimmer width={isMobile ? 80 : "100%"} height={24} borderRadius={6} />
            {!isMobile && <SkeletonShimmer width="100%" height={24} borderRadius={6} />}
          </View>
        </View>
      </View>
    </View>
  )
}

// ============================================================================
// Styles (matching FleetOrderRow COMPACT design)
// ============================================================================

const $cardOuter: ViewStyle = {
  marginBottom: 10, // COMPACT: reduced from 16
  borderRadius: 14,
  backgroundColor: colors.surface,
  shadowColor: "rgba(17, 30, 44, 0.08)",
  shadowOffset: { width: 0, height: 2 }, // COMPACT: reduced from 4
  shadowOpacity: 1,
  shadowRadius: 12, // COMPACT: reduced from 16
  elevation: 3, // COMPACT: reduced from 4
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.06)",
  overflow: "hidden",
  position: "relative",
}

const $cardInner: ViewStyle = {
  backgroundColor: colors.surface, // COMPACT: solid white
  paddingLeft: 14, // COMPACT: reduced from 20
  paddingRight: 14,
  paddingTop: 12, // COMPACT: reduced from 20
  paddingBottom: 12,
}

const $contentContainer: ViewStyle = {
  flexDirection: "row",
  gap: 10, // COMPACT: reduced from 16
}

const $contentContainerMobile: ViewStyle = {
  flexDirection: "column",
  gap: spacing.sm,
}

// ============================================================================
// Left Section (License Plate Area)
// ============================================================================

const $leftSection: ViewStyle = {
  width: 150, // COMPACT: fixed width instead of minWidth
  flexShrink: 0,
  justifyContent: "space-between", // COMPACT: space-between for proper layout
  alignItems: "flex-start",
  paddingRight: 15,
  paddingVertical: 6, // COMPACT: reduced from 8
}

const $leftSectionMobile: ViewStyle = {
  width: "100%",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingVertical: 6,
}

const $leftContent: ViewStyle = {
  gap: 8,
  alignItems: "flex-start",
}

const $orderNumber: ViewStyle = {
  marginBottom: 2,
}

// ============================================================================
// Center Section (Status & Details)
// ============================================================================

const $centerSection: ViewStyle = {
  flex: 1,
  gap: 6, // COMPACT: reduced from 12
  paddingHorizontal: 10, // COMPACT: reduced from 12
  paddingVertical: 6, // COMPACT: reduced from 8
}

const $progressBar: ViewStyle = {
  marginBottom: 4,
}

const $dateRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
}

const $badge: ViewStyle = {
  marginTop: 4,
}

// ============================================================================
// Right Section (Action Buttons) - VERTICAL STACK
// ============================================================================

const $rightSection: ViewStyle = {
  width: 165, // COMPACT: fixed width for buttons
  flexShrink: 0,
  flexDirection: "column", // CRITICAL: vertical stack (not row)
  gap: 4, // COMPACT: tight spacing between buttons
  alignItems: "stretch", // Full-width buttons
  paddingLeft: 8,
  paddingVertical: 6, // COMPACT: reduced from 8
}

const $rightSectionMobile: ViewStyle = {
  width: "100%",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
  paddingVertical: 6,
}
